import { useState } from 'react';
import { fmtTs } from '../lib/globalTz';

/**
 * User Report — Sentinel-Workbook-style render for an entityType=user
 * investigation. The backend resolved the identity (AccountObjectId, name,
 * recent devices) and ran ~16 parameterised KQL panels in parallel; this
 * component slots each section into its visual home (identity card, sign-in
 * geo, Sentinel incidents, Entra risk, Admin PIM, sign-ins by device/app,
 * audit/Azure/O365 activity, email flow).
 *
 * Design notes:
 *   - Sections come keyed by `id` from the backend so reordering / adding /
 *     removing on the server is a one-side change.
 *   - Empty / not-deployed / errored sections still render — they're framed
 *     in a subdued style so the analyst sees what's missing without losing
 *     the layout. Sections with hits get the standard card treatment.
 *   - Identity card is rendered specially (key/value pairs); everything else
 *     uses the generic table renderer.
 *   - Sign-in geolocation and Email Flow get bespoke renderings: the geo
 *     panel becomes a horizontal-bar-by-country list, and Email Flow becomes
 *     a 4-tile counter strip.
 */

export interface ResolvedIdentity {
  accountObjectId: string;
  accountUpn: string;
  accountName: string;
  accountUpnSuffix: string;
  recentDevices: string[];
}

export interface SectionResult {
  id: string;
  title: string;
  description: string;
  table: string;
  joinedTables?: string[];
  kql: string;
  status: 'ok' | 'missing' | 'error';
  rowCount: number;
  truncated: boolean;
  columns?: string[];
  rows?: unknown[][];
  error?: string;
}

export interface UserReportPayload {
  kind: 'userReport';
  customerId: string;
  entityType: 'user';
  value: string;
  days: number;
  rowCap: number;
  identity: ResolvedIdentity;
  identityKql: string;
  recentDevicesKql: string;
  summary: { matched: number; empty: number; missing: number; errored: number; totalRows: number };
  sections: SectionResult[];
}

interface Props {
  report: UserReportPayload;
  onExport: () => void;
}

export function UserReportView({ report, onExport }: Props) {
  const sectionsById = Object.fromEntries(report.sections.map((s) => [s.id, s]));
  const get = (id: string): SectionResult | undefined => sectionsById[id];

  return (
    <div className="space-y-3">
      {/* ── Summary header ──────────────────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-muted/20 p-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">
              User report for{' '}
              <span className="font-mono break-all text-foreground font-bold">{report.identity.accountUpn || report.value}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Lookback: {report.days}d · Row cap per section: {report.rowCap} · ObjectId{' '}
              <span className="font-mono text-foreground">{report.identity.accountObjectId.slice(0, 8)}…</span>
            </div>
          </div>
          <button
            onClick={onExport}
            className="text-[11px] font-semibold px-2.5 py-1 rounded border border-border bg-card hover:bg-muted text-foreground shrink-0"
          >
            ↓ Export JSON
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Pill label="Sections with hits" value={report.summary.matched} tone="hit" />
          <Pill label="Empty" value={report.summary.empty} tone="muted" />
          <Pill label="Not deployed" value={report.summary.missing} tone="warn" />
          <Pill label="Errors" value={report.summary.errored} tone="bad" />
          <Pill label="Total rows" value={report.summary.totalRows} tone="muted" />
        </div>
      </div>

      {/* ── Identity card + recent devices ──────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <IdentityCard identity={report.identity} section={get('identityCard')} />
        </div>
        <div>
          <RecentDevicesCard devices={report.identity.recentDevices} />
        </div>
      </div>

      {/* ── Sign-in success/failure trend (full width) ───────────────────── */}
      <SignInTrendCard section={get('signInTrend')} />

      {/* ── Sign-in geo + email flow side-by-side ───────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SignInGeoCard section={get('signinGeo')} />
        <EmailFlowCard section={get('emailFlow')} />
      </div>

      {/* ── Sentinel + Entra Risk ────────────────────────────────────────── */}
      <SectionCard section={get('sentinelIncidents')} />
      <SectionCard section={get('riskDetections')} />
      <SectionCard section={get('adminPim')} />
      <SectionCard section={get('passwordReset')} />

      {/* ── Sign-ins by device + by app ──────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SectionCard section={get('loginByDevice')} compact />
        <SectionCard section={get('loginByApp')} compact />
      </div>

      {/* ── Audit / Azure / O365 ─────────────────────────────────────────── */}
      <SectionCard section={get('azureAuditActivity')} />
      <SectionCard section={get('azureActivity')} />
      <SectionCard section={get('o365Activity')} />

      {/* ── Email panels ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SectionCard section={get('emailRecipients')} compact />
        <SectionCard section={get('emailSenders')} compact />
      </div>
      <SectionCard section={get('emailFreemail')} />
      <SectionCard section={get('emailBlocked')} />
      <SectionCard section={get('emailThreats')} />
    </div>
  );
}

function Pill({ label, value, tone }: { label: string; value: number; tone: 'hit' | 'muted' | 'warn' | 'bad' }) {
  const cls = {
    hit: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    muted: 'bg-muted/40 text-muted-foreground border-border',
    warn: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    bad: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
  }[tone];
  return (
    <div className={`px-2.5 py-1 rounded border text-[11px] ${cls}`}>
      <span className="font-bold">{value}</span> <span>{label}</span>
    </div>
  );
}

// ── Identity card ─────────────────────────────────────────────────────────────

function IdentityCard({ identity, section }: { identity: ResolvedIdentity; section?: SectionResult }) {
  // Map the single-row identity result into a {column → value} record.
  const facts: Record<string, string> = {};
  if (section?.status === 'ok' && section.columns && section.rows && section.rows.length > 0) {
    const row = section.rows[0] as unknown[];
    section.columns.forEach((c, i) => {
      const v = row[i];
      facts[c] = v == null ? '' : String(v);
    });
  }

  const initials = (facts['AccountDisplayName'] || identity.accountUpn || '?')
    .split(/\s+|@|\./)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || '?';

  const enabled = String(facts['IsAccountEnabled'] ?? '').toLowerCase();
  const enabledBadge =
    enabled === 'true' ? (
      <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40">
        Enabled
      </span>
    ) : enabled === 'false' ? (
      <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-red-500/20 text-red-700 dark:text-red-300 border border-red-500/40">
        Disabled
      </span>
    ) : null;

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary font-bold text-base shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-foreground">{facts['AccountDisplayName'] || identity.accountUpn || '-'}</span>
            {enabledBadge}
            {facts['RiskLevel'] && facts['RiskLevel'] !== 'none' && facts['RiskLevel'] !== '' && (
              <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40">
                Risk: {facts['RiskLevel']}
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground font-mono break-all mt-0.5">{identity.accountUpn}</div>
          {section?.status === 'missing' && (
            <div className="text-[11px] text-amber-700 dark:text-amber-300 mt-1 italic">IdentityInfo table not deployed in this workspace.</div>
          )}
          {section?.status === 'error' && (
            <div className="text-[11px] text-red-700 dark:text-red-300 mt-1 italic break-all">{section.error}</div>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
        <Field label="Job Title" value={facts['JobTitle']} />
        <Field label="Department" value={facts['Department']} />
        <Field label="Manager" value={facts['Manager']} />
        <Field label="Company" value={facts['CompanyName']} />
        <Field label="City" value={facts['City']} />
        <Field label="Country" value={facts['Country']} />
        <Field label="Account Name" value={identity.accountName} />
        <Field label="Domain" value={facts['AccountDomain']} />
        <Field label="Object ID" value={identity.accountObjectId} mono />
        <Field label="SID" value={facts['AccountSID']} mono />
        <Field label="Last Successful Sign-in" value={fmtDate(facts['LastSuccessfulSignIn'])} />
        <Field label="Last Failed Sign-in" value={fmtDate(facts['LastFailedSignIn'])} />
        <Field label="Sign-in Count" value={facts['SignInCount']} />
        <Field label="Failed Sign-in Count" value={facts['FailedSignInCount']} />
      </div>

      {(facts['AssignedRoles'] && facts['AssignedRoles'] !== '[]') ? (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">Assigned Roles</div>
          <div className="text-[11px] font-mono text-foreground bg-background border border-border rounded p-2 break-words">
            {facts['AssignedRoles']}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-foreground truncate ${mono ? 'font-mono text-[10px]' : ''}`} title={value || ''}>
        {value && value.length > 0 ? value : '-'}
      </div>
    </div>
  );
}

function fmtDate(s?: string): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

// ── Recent devices ────────────────────────────────────────────────────────────

function RecentDevicesCard({ devices }: { devices: string[] }) {
  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4 h-full">
      <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Recent Devices</div>
      {devices.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">No devices observed in sign-in logs for this window.</div>
      ) : (
        <ul className="space-y-1">
          {devices.map((d) => (
            <li key={d} className="text-[11px] font-mono text-foreground bg-background border border-border rounded px-2 py-1 truncate" title={d}>
              {d}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Sign-in geolocation panel ─────────────────────────────────────────────────

function SignInGeoCard({ section }: { section?: SectionResult }) {
  if (!section) return null;
  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4">
      <SectionHeader section={section} />
      {renderSectionStatusOnly(section) ?? (
        <SignInGeoBars columns={section.columns ?? []} rows={section.rows ?? []} />
      )}
    </div>
  );
}

function SignInGeoBars({ columns, rows }: { columns: string[]; rows: unknown[][] }) {
  const ci = (n: string) => columns.indexOf(n);
  const cnIdx = ci('Country');
  const cityIdx = ci('City');
  const stateIdx = ci('State');
  const cntIdx = ci('SignInCount');
  const failIdx = ci('FailedCount');
  const ipsIdx = ci('UniqueIPs');

  if (cnIdx < 0 || cntIdx < 0) {
    return <GenericTable columns={columns} rows={rows} />;
  }

  const max = Math.max(1, ...rows.map((r) => Number((r as unknown[])[cntIdx] ?? 0)));

  return (
    <div className="mt-2 space-y-1.5">
      {rows.slice(0, 20).map((r, i) => {
        const row = r as unknown[];
        const country = String(row[cnIdx] ?? '');
        const city = cityIdx >= 0 ? String(row[cityIdx] ?? '') : '';
        const state = stateIdx >= 0 ? String(row[stateIdx] ?? '') : '';
        const count = Number(row[cntIdx] ?? 0);
        const failed = failIdx >= 0 ? Number(row[failIdx] ?? 0) : 0;
        const ips = ipsIdx >= 0 ? Number(row[ipsIdx] ?? 0) : 0;
        const pct = (count / max) * 100;
        return (
          <div key={i} className="flex items-center gap-2 text-[11px]">
            <div className="w-40 shrink-0 truncate text-foreground" title={`${country} · ${city}${state ? `, ${state}` : ''}`}>
              <span className="font-bold">{country || '-'}</span>
              {(city || state) && <span className="text-muted-foreground"> · {city}{state ? `, ${state}` : ''}</span>}
            </div>
            <div className="flex-1 h-3 bg-background border border-border rounded relative overflow-hidden">
              <div
                className={`h-full ${failed > 0 ? 'bg-amber-500/40' : 'bg-emerald-500/40'}`}
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            </div>
            <div className="w-16 text-right text-foreground tabular-nums shrink-0">
              {count.toLocaleString()}
              {failed > 0 && (
                <span className="text-amber-600 dark:text-amber-300 text-[10px] ml-1" title="Failed sign-ins">({failed} failed)</span>
              )}
            </div>
            <div className="w-12 text-right text-muted-foreground tabular-nums shrink-0 text-[10px]">{ips} IP</div>
          </div>
        );
      })}
      {rows.length > 20 && (
        <div className="text-[10px] text-muted-foreground italic mt-1">+{rows.length - 20} more locations. Use Export JSON for the full list.</div>
      )}
    </div>
  );
}

// ── Sign-in success/failure trend ────────────────────────────────────────────
//
// Renders the bucketed Success/Failure counts from the `signInTrend` section
// as a stacked column chart. SVG is intentional — keeps zero new deps and
// stays crisp at any zoom level. Failures sit on top of successes so a sudden
// burst of red is visually unmissable (password spray, lockout cascade, etc).

function SignInTrendCard({ section }: { section?: SectionResult }) {
  if (!section) return null;
  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4">
      <SectionHeader section={section} />
      {renderSectionStatusOnly(section) ?? (
        <SignInTrendChart columns={section.columns ?? []} rows={section.rows ?? []} />
      )}
    </div>
  );
}

function SignInTrendChart({ columns, rows }: { columns: string[]; rows: unknown[][] }) {
  const bIdx = columns.indexOf('Bucket');
  const oIdx = columns.indexOf('Outcome');
  const cIdx = columns.indexOf('Count');
  if (bIdx < 0 || oIdx < 0 || cIdx < 0) {
    return <GenericTable columns={columns} rows={rows} />;
  }

  // Pivot rows -> { bucket: ts, success, failure }. KQL never emits both
  // outcomes for the same bucket as a single row, so we accumulate.
  const byBucket = new Map<string, { ts: number; success: number; failure: number }>();
  for (const r of rows) {
    const row = r as unknown[];
    const bucket = String(row[bIdx] ?? '');
    if (!bucket) continue;
    const ts = new Date(bucket).getTime();
    if (Number.isNaN(ts)) continue;
    const outcome = String(row[oIdx] ?? '').toLowerCase();
    const count = Number(row[cIdx] ?? 0);
    const slot = byBucket.get(bucket) ?? { ts, success: 0, failure: 0 };
    if (outcome === 'success') slot.success += count;
    else slot.failure += count;
    byBucket.set(bucket, slot);
  }
  const buckets = Array.from(byBucket.values()).sort((a, b) => a.ts - b.ts);
  if (buckets.length === 0) {
    return <div className="mt-2 text-[11px] text-muted-foreground italic">No sign-ins in window.</div>;
  }
  const maxTotal = Math.max(1, ...buckets.map((b) => b.success + b.failure));
  const totalSuccess = buckets.reduce((s, b) => s + b.success, 0);
  const totalFailure = buckets.reduce((s, b) => s + b.failure, 0);

  // SVG dimensions — fixed viewBox, scales responsively.
  const W = 800;
  const H = 160;
  const padL = 32;
  const padR = 8;
  const padT = 8;
  const padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barW = Math.max(1, innerW / buckets.length - 1);

  const fmtTick = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit' });
  };

  // Pick ~6 evenly-spaced x-axis ticks so labels never overlap.
  const tickCount = Math.min(6, buckets.length);
  const tickIdx = Array.from({ length: tickCount }, (_, i) =>
    Math.round((i * (buckets.length - 1)) / Math.max(1, tickCount - 1)),
  );

  return (
    <div className="mt-2">
      <div className="flex items-center gap-3 text-[11px] mb-2">
        <span className="flex items-center gap-1 text-muted-foreground">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500/70 border border-emerald-500/80" />
          Success <span className="font-bold text-foreground tabular-nums">{totalSuccess.toLocaleString()}</span>
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <span className="inline-block w-3 h-3 rounded-sm bg-red-500/70 border border-red-500/80" />
          Failure <span className="font-bold text-foreground tabular-nums">{totalFailure.toLocaleString()}</span>
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{buckets.length} buckets · peak {maxTotal.toLocaleString()}/bucket</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40 bg-background border border-border rounded">
        {/* Y-axis baseline */}
        <line x1={padL} y1={padT + innerH} x2={padL + innerW} y2={padT + innerH} stroke="currentColor" className="text-border" strokeWidth={1} />
        {/* Y-axis label (max) */}
        <text x={padL - 4} y={padT + 8} textAnchor="end" className="fill-muted-foreground" fontSize={9}>
          {maxTotal.toLocaleString()}
        </text>
        <text x={padL - 4} y={padT + innerH - 1} textAnchor="end" className="fill-muted-foreground" fontSize={9}>
          0
        </text>
        {buckets.map((b, i) => {
          const total = b.success + b.failure;
          const totalH = (total / maxTotal) * innerH;
          const failH = (b.failure / maxTotal) * innerH;
          const sucH = totalH - failH;
          const x = padL + i * (innerW / buckets.length) + 0.5;
          const yTop = padT + innerH - totalH;
          const tip = `${fmtTs(b.ts)}\nSuccess: ${b.success.toLocaleString()}\nFailure: ${b.failure.toLocaleString()}`;
          return (
            <g key={i}>
              <title>{tip}</title>
              {/* Success (bottom, green) */}
              <rect
                x={x}
                y={padT + innerH - sucH}
                width={barW}
                height={sucH}
                className="fill-emerald-500/70"
              />
              {/* Failure (top, red) */}
              <rect
                x={x}
                y={yTop}
                width={barW}
                height={failH}
                className="fill-red-500/80"
              />
            </g>
          );
        })}
        {/* X-axis tick labels */}
        {tickIdx.map((i) => {
          const x = padL + i * (innerW / buckets.length) + barW / 2;
          return (
            <text
              key={i}
              x={x}
              y={H - 6}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize={9}
            >
              {fmtTick(buckets[i]!.ts)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// ── Email flow tile ───────────────────────────────────────────────────────────

function EmailFlowCard({ section }: { section?: SectionResult }) {
  if (!section) return null;
  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4">
      <SectionHeader section={section} />
      {renderSectionStatusOnly(section) ?? (
        <EmailFlowTiles columns={section.columns ?? []} rows={section.rows ?? []} />
      )}
    </div>
  );
}

function EmailFlowTiles({ columns, rows }: { columns: string[]; rows: unknown[][] }) {
  if (rows.length === 0) {
    return <div className="text-[11px] text-muted-foreground italic mt-2">No email activity in window.</div>;
  }
  const row = rows[0] as unknown[];
  const get = (n: string) => {
    const i = columns.indexOf(n);
    return i >= 0 ? Number(row[i] ?? 0) : 0;
  };
  const tiles = [
    { label: 'Inbound: Internal', value: get('InternalInbound'), tone: 'muted' as const },
    { label: 'Inbound: External', value: get('ExternalInbound'), tone: 'warn' as const },
    { label: 'Outbound: Internal', value: get('InternalOutbound'), tone: 'muted' as const },
    { label: 'Outbound: External', value: get('ExternalOutbound'), tone: 'warn' as const },
  ];
  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      {tiles.map((t) => (
        <div
          key={t.label}
          className={`rounded border p-2 text-center ${
            t.tone === 'warn'
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-background border-border'
          }`}
        >
          <div className="text-lg font-bold text-foreground tabular-nums">{t.value.toLocaleString()}</div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Generic section card ──────────────────────────────────────────────────────

function SectionCard({ section, compact }: { section?: SectionResult; compact?: boolean }) {
  const [showKql, setShowKql] = useState(false);
  if (!section) return null;

  const headerOnly = section.status !== 'ok' || section.rowCount === 0;

  return (
    <div className="rounded-lg border border-border bg-muted/10 p-4">
      <SectionHeader section={section} />

      {headerOnly ? (
        renderSectionStatusOnly(section)
      ) : (
        <GenericTable
          columns={section.columns ?? []}
          rows={section.rows ?? []}
          maxRows={compact ? 15 : 50}
          truncated={section.truncated}
        />
      )}

      <div className="mt-2">
        <button
          type="button"
          onClick={() => setShowKql((v) => !v)}
          className="text-[10px] text-muted-foreground hover:text-foreground"
        >
          {showKql ? '▾' : '▸'} View KQL
        </button>
        {showKql && (
          <pre className="mt-1 text-[10px] font-mono bg-background border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
            {section.kql}
          </pre>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ section }: { section: SectionResult }) {
  const badge = (() => {
    if (section.status === 'missing') {
      return <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40">Not deployed</span>;
    }
    if (section.status === 'error') {
      return <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-red-500/20 text-red-700 dark:text-red-300 border border-red-500/40">Error</span>;
    }
    if (section.rowCount === 0) {
      return <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground border border-border">No matches</span>;
    }
    return (
      <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40">
        {section.truncated ? `${section.rowCount}+ rows` : `${section.rowCount} row${section.rowCount === 1 ? '' : 's'}`}
      </span>
    );
  })();

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-foreground">{section.title}</span>
          {badge}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{section.description}</div>
        <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
          {section.table}{section.joinedTables && section.joinedTables.length > 0 ? ` ⨝ ${section.joinedTables.join(', ')}` : ''}
        </div>
      </div>
    </div>
  );
}

function renderSectionStatusOnly(section: SectionResult) {
  if (section.status === 'missing') {
    return (
      <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300 italic">
        This table is not deployed in the customer's workspace. No data available.
      </div>
    );
  }
  if (section.status === 'error') {
    return (
      <div className="mt-2 px-2 py-1.5 bg-red-500/10 text-red-700 dark:text-red-300 text-[11px] rounded border border-red-500/30 whitespace-pre-wrap break-words font-mono">
        {section.error}
      </div>
    );
  }
  if (section.status === 'ok' && section.rowCount === 0) {
    return <div className="mt-2 text-[11px] text-muted-foreground italic">No matching rows in the selected window.</div>;
  }
  return null;
}

function GenericTable({
  columns,
  rows,
  maxRows = 50,
  truncated,
}: {
  columns: string[];
  rows: unknown[][];
  maxRows?: number;
  truncated?: boolean;
}) {
  // Prioritise the most-useful columns first so analysts see timestamps + key
  // identifiers without horizontal scrolling.
  const interesting = [
    'TimeGenerated', 'PimStart', 'PimEnd', 'DetectedDateTime', 'ActivityDateTime',
    'IncidentNumber', 'Title', 'Severity', 'Status', 'AlertName', 'AlertSeverity',
    'OperationName', 'Operation', 'OfficeWorkload', 'AppDisplayName', 'DeviceName',
    'AccountUpn', 'AccountName', 'UserPrincipalName', 'Caller',
    'SenderFromAddress', 'RecipientEmailAddress', 'Subject', 'DeliveryAction',
    'ThreatTypes', 'IpAddress', 'CallerIpAddress', 'IPAddress',
    'Count', 'SignInCount', 'LoginCount', 'LastSeen',
  ];
  const ordered = [
    ...interesting.filter((c) => columns.includes(c)),
    ...columns.filter((c) => !interesting.includes(c)),
  ];
  const indices = ordered.map((c) => columns.indexOf(c));

  return (
    <div className="mt-2">
      <div className="overflow-x-auto border border-border rounded">
        <table className="w-full text-[11px]">
          <thead className="bg-muted/40 sticky top-0">
            <tr>
              {ordered.map((c) => (
                <th key={c} className="text-left px-2 py-1 font-semibold text-foreground whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, maxRows).map((row, i) => (
              <tr key={i} className="border-t border-border hover:bg-muted/20">
                {indices.map((idx, j) => (
                  <td
                    key={j}
                    className="px-2 py-1 align-top font-mono text-[10px] max-w-[280px] truncate text-foreground"
                    title={formatCell((row as unknown[])[idx])}
                  >
                    {formatCell((row as unknown[])[idx])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(rows.length > maxRows || truncated) && (
        <p className="mt-1 text-[10px] text-muted-foreground italic">
          Showing first {maxRows} rows
          {truncated ? ` of ${rows.length}+ (server cap reached. Narrow the window for a complete view.)` : rows.length > maxRows ? ` of ${rows.length}` : ''}.
          Use Export JSON above for the full result set.
        </p>
      )}
    </div>
  );
}

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') {
    if (ISO_DATETIME_RE.test(v)) return fmtTs(v);
    return v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
